import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Button,
  Card,
  DatePicker,
  Divider,
  InputSelect,
  RadixTable,
  TColumn,
} from '@features/ui';
import {
  TReportLeaderBoardParams,
  TReportLeaderBoardRow,
  TLeaderBoardPeriod,
} from '@src/types';
import { exportLeaderBoard, getLeaderBoard } from '@src/services/reports.ts';
import { downloadFile } from '@lib/utils.ts';
import { useProductOptions } from '@hooks/useProductOptions.tsx';
import { ExportIcon } from '@features/ui/icons';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const SHOW_TOP_OPTIONS = [
  { label: '10', value: '10' },
  { label: '25', value: '25' },
  { label: '50', value: '50' },
  { label: '100', value: '100' },
  { label: '250', value: '250' },
];

const PERIOD_OPTIONS = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
];

type LeaderBoardFilterForm = {
  product_id: string;
  show_top: string;
  period: string;
  date: Date | undefined;
};

export const LeaderBoard = () => {
  const navigate = useNavigate();
  const { productOptions } = useProductOptions();
  const [data, setData] = useState<TReportLeaderBoardRow[]>([]);

  const { control, handleSubmit, getValues } = useForm<LeaderBoardFilterForm>({
    defaultValues: {
      product_id: '',
      show_top: '10',
      period: 'daily',
      date: new Date(),
    },
  });

  const buildPayload = (
    values: LeaderBoardFilterForm
  ): TReportLeaderBoardParams => ({
    product_id: values.product_id ? Number(values.product_id) : null,
    show_top: Number(values.show_top),
    period: values.period as TLeaderBoardPeriod,
    date: (values.date ?? new Date()).toISOString(),
  });

  const handleGetData = async (values: LeaderBoardFilterForm) => {
    try {
      const res = await getLeaderBoard(buildPayload(values));

      setData(res.data);
      toast.success('Leaderboard data fetched successfully');
    } catch (error) {
      toast.error('Failed to fetch leaderboard data. Please try again.');
      console.error('Error fetching leaderboard data:', error);
    }
  };

  const handleExport = async () => {
    try {
      const res = await exportLeaderBoard(buildPayload(getValues()));

      downloadFile(res.data, 'leaderboard');
    } catch (error) {
      toast.error('Failed to export leaderboard. Please try again.');
      console.error('Error exporting leaderboard:', error);
    }
  };

  const columns = useMemo(
    (): TColumn<TReportLeaderBoardRow>[] => [
      { title: 'Rank', render: (item) => <p>{item?.rank}</p> },
      { title: 'User', render: (item) => <p>{item?.user_display_name}</p> },
      {
        title: 'Username',
        render: (item) => (
          <p
            className={'text-blue'}
            onClick={() => navigate(`/users/${item?.user_id}`)}
          >
            {item?.user_name}
          </p>
        ),
      },
      { title: 'Account', render: (item) => <p>{item?.account}</p> },
      { title: 'Profit', render: (item) => <p>{item?.profit}</p> },
      { title: 'Balance', render: (item) => <p>{item?.balance}</p> },
      { title: 'Trades', render: (item) => <p>{item?.trades}</p> },
      { title: 'Win %', render: (item) => <p>{item?.win_rate}%</p> },
    ],
    []
  );

  return (
    <div>
      <div
        className={
          'flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col'
        }
      >
        <h1 className={'text-[23px]'}>Leaderboard</h1>
        <Button
          className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit'}
          variant={'outline'}
          onClick={handleExport}
        >
          <p>Export</p>
          <ExportIcon />
        </Button>
      </div>
      <Card className={'mt-4.5'}>
        <form onSubmit={handleSubmit(handleGetData)}>
          <div
            className={
              'flex justify-between items-end lg:flex-row flex-col mb-5 gap-6'
            }
          >
            <div className={'w-full flex gap-6 flex-wrap items-end'}>
              <Controller
                control={control}
                name="product_id"
                render={({ field }) => (
                  <div className={'flex flex-col gap-1'}>
                    <span className={'text-sm text-muted-foreground'}>
                      Package
                    </span>
                    <InputSelect
                      allowClear
                      className={'min-w-[200px]'}
                      options={productOptions}
                      placeholder={'Select package'}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
              <Controller
                control={control}
                name="show_top"
                render={({ field }) => (
                  <div className={'flex flex-col gap-1'}>
                    <span className={'text-sm text-muted-foreground'}>
                      Show Top
                    </span>
                    <InputSelect
                      className={'min-w-[120px]'}
                      options={SHOW_TOP_OPTIONS}
                      placeholder={'Show Top'}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
              <Controller
                control={control}
                name="period"
                render={({ field }) => (
                  <div className={'flex flex-col gap-1'}>
                    <span className={'text-sm text-muted-foreground'}>
                      Period
                    </span>
                    <InputSelect
                      className={'min-w-[140px]'}
                      options={PERIOD_OPTIONS}
                      placeholder={'Period'}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
              <Controller
                control={control}
                name="date"
                render={({ field }) => (
                  <div className={'flex flex-col gap-1'}>
                    <span className={'text-sm text-muted-foreground'}>
                      Date
                    </span>
                    <DatePicker
                      className={'min-w-[160px]'}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
            </div>
            <div className={'flex gap-5'}>
              <Button size={'sm'} type="submit" variant={'lime'}>
                Get
              </Button>
            </div>
          </div>
        </form>
        <Divider />
        <RadixTable columns={columns} data={data} />
      </Card>
    </div>
  );
};
